import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import LiveFleetMap from '../components/LiveFleetMap';
import useLiveFleet from '../components/useLiveFleet';
import { useRealtime } from '../providers/RealtimeProvider';
import { getFare } from '../services/fareService';
import { getOperationsIntelligence } from '../services/intelligenceService';
import { getDriverSafetyReport } from '../services/safetyService';

const BRIEFING_WINDOW_HOURS = 12;
const BRIEFING_REFRESH_DELAY_MS = 1_200;

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const isAssigned = (booking) => Boolean(booking?.driverId || booking?.cabNumber);

const unwrapFare = (response) => {
  const payload = response?.data || {};
  const fare = payload.fare || payload.currentFare || payload.data || payload;
  if (!fare || Array.isArray(fare) || typeof fare !== 'object') return null;
  const fareFields = ['farePerMile', 'extraPass', 'waitTimePerMinute', 'baseFare', 'minimumFare'];
  return fareFields.some((field) => Object.prototype.hasOwnProperty.call(fare, field)) ? fare : null;
};

const postureCopy = (posture) => {
  if (posture === 'critical') return { label: 'Dispatch action required', tone: 'critical' };
  if (posture === 'intervention') return { label: 'Intervention needed', tone: 'critical' };
  if (posture === 'watch') return { label: 'Watch the next window', tone: 'watch' };
  return { label: 'Operations are on track', tone: 'clear' };
};

const MetricTile = ({ label, value, hint, tone = 'neutral' }) => (
  <div className={`ops-briefing-metric ops-briefing-metric--${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    <em>{hint}</em>
  </div>
);

const EmptyState = ({ children }) => <div className="ops-empty">{children}</div>;

const compactHourLabel = (hour) => {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
};

const HourlyTripChart = ({ profile }) => {
  if (!profile?.hours?.length) {
    return <EmptyState>Hourly trip activity is unavailable until the briefing refreshes.</EmptyState>;
  }
  if (!profile.total) {
    return <EmptyState>No picked-up or completed trips have been recorded today.</EmptyState>;
  }

  const maxCount = Math.max(...profile.hours.map((item) => Number(item.count || 0)), 1);

  return (
    <>
      <div className="ops-hourly-summary">
        <div>
          <strong>{formatNumber(profile.total)}</strong>
          <span>trip start{profile.total === 1 ? '' : 's'} today</span>
        </div>
        <div>
          <span>Peak hour</span>
          <strong>{profile.peakLabel} - {formatNumber(profile.peakCount)} trips</strong>
        </div>
      </div>
      <div
        className="ops-hourly-chart"
        role="img"
        aria-label={`${profile.total} trip starts today. Peak hour ${profile.peakLabel} with ${profile.peakCount} trips.`}
      >
        {profile.hours.map((item) => {
          const count = Number(item.count || 0);
          const height = count ? Math.max(8, Math.round((count / maxCount) * 100)) : 0;
          const showLabel = item.hour % 4 === 0;
          return (
            <div
              key={item.hour}
              className={[
                'ops-hourly-column',
                item.isCurrent ? 'is-current' : '',
                item.isPeak ? 'is-peak' : '',
                !item.isElapsed ? 'is-future' : '',
              ].filter(Boolean).join(' ')}
              title={`${item.label}: ${count} trip${count === 1 ? '' : 's'}`}
              aria-label={`${item.label}: ${count} trip${count === 1 ? '' : 's'}`}
            >
              <span className="ops-hourly-count">{count || ''}</span>
              <div className="ops-hourly-track"><i style={{ height: `${height}%` }} /></div>
              <span className="ops-hourly-label">{showLabel ? compactHourLabel(item.hour) : ''}</span>
            </div>
          );
        })}
      </div>
    </>
  );
};

const Dashboard = () => {
  const { socket, connected } = useRealtime();
  const fleet = useLiveFleet();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [backgroundError, setBackgroundError] = useState('');
  const [fare, setFare] = useState(null);
  const [fareLoading, setFareLoading] = useState(true);
  const [fareRefreshing, setFareRefreshing] = useState(false);
  const [fareError, setFareError] = useState('');
  const [safety, setSafety] = useState(null);
  const briefingRequestRef = useRef(null);
  const briefingTimerRef = useRef(null);
  const hasConnectedRef = useRef(false);

  const loadBriefing = useCallback(async ({ silent = false } = {}) => {
    if (briefingRequestRef.current) return briefingRequestRef.current;

    if (silent) setRefreshing(true);
    else setLoading(true);
    if (!silent) setError('');

    const request = getOperationsIntelligence({ windowHours: BRIEFING_WINDOW_HOURS });
    briefingRequestRef.current = request;

    try {
      const response = await request;
      const nextData = response?.data || null;
      setData(nextData);
      setError('');
      setBackgroundError('');
      return nextData;
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to load the operations briefing right now.';
      if (silent) setBackgroundError(message);
      else setError(message);
      return null;
    } finally {
      briefingRequestRef.current = null;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const scheduleBriefingRefresh = useCallback(() => {
    if (briefingTimerRef.current) clearTimeout(briefingTimerRef.current);
    briefingTimerRef.current = setTimeout(() => {
      briefingTimerRef.current = null;
      loadBriefing({ silent: true });
    }, BRIEFING_REFRESH_DELAY_MS);
  }, [loadBriefing]);

  const loadFare = useCallback(async ({ silent = false } = {}) => {
    if (silent) setFareRefreshing(true);
    else setFareLoading(true);
    setFareError('');

    try {
      const response = await getFare();
      setFare(unwrapFare(response));
    } catch (err) {
      setFareError(err.response?.data?.message || 'Fare system unavailable.');
    } finally {
      setFareLoading(false);
      setFareRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadBriefing();
  }, [loadBriefing]);

  useEffect(() => {
    loadFare();
  }, [loadFare]);

  const loadSafety = useCallback(async () => {
    try {
      const month = new Date().toISOString().slice(0, 7);
      const response = await getDriverSafetyReport({ month, limit: 100 });
      setSafety(response.data?.summary || null);
    } catch {
      // Safety summary is supplementary; the operations dashboard stays usable.
    }
  }, []);

  useEffect(() => { loadSafety(); }, [loadSafety]);

  useEffect(() => () => {
    if (briefingTimerRef.current) clearTimeout(briefingTimerRef.current);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on('assignment:updated', scheduleBriefingRefresh);
    socket.on('driver:presence', scheduleBriefingRefresh);
    socket.on('hos:alert', scheduleBriefingRefresh);
    return () => {
      socket.off('assignment:updated', scheduleBriefingRefresh);
      socket.off('driver:presence', scheduleBriefingRefresh);
      socket.off('hos:alert', scheduleBriefingRefresh);
    };
  }, [scheduleBriefingRefresh, socket]);

  useEffect(() => {
    if (!connected) return;
    if (hasConnectedRef.current) scheduleBriefingRefresh();
    else hasConnectedRef.current = true;
  }, [connected, scheduleBriefingRefresh]);

  const handleRefresh = useCallback(() => {
    loadBriefing({ silent: true });
    loadFare({ silent: true });
    fleet.refresh().catch(() => {});
    loadSafety();
  }, [fleet, loadBriefing, loadFare, loadSafety]);

  const headerActions = useMemo(() => (
    <>
      <Link to="/bookings/new" className="btn btn-primary">
        <span className="icon">+</span>
        New booking
      </Link>
      <Link to="/operations/dispatch" className="btn btn-ghost">Dispatch map</Link>
    </>
  ), []);

  const metrics = data?.metrics || {};
  const readiness = data?.dispatchReadiness || {};
  const posture = postureCopy(data?.posture);
  const pickups = (data?.upcomingBookings || readiness.upcomingPreview || []).slice(0, 5);
  const hourlyTrips = data?.todayHourlyTrips || null;
  const hasBriefing = Boolean(data);
  const dispatchExceptions = readiness.exceptionCount ?? (
    Number(metrics.overduePickups || 0) +
    Number(metrics.needsReassignment || 0) +
    Number(metrics.unassignedUpcoming || 0)
  );

  const renderSkeleton = () => (
    <>
      <div className="ops-status-banner skeleton" style={{ minHeight: 152 }} />
      <div className="ops-briefing-metrics">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="ops-briefing-metric skeleton" style={{ minHeight: 112 }} />
        ))}
      </div>
      <div className="ops-primary-grid">
        <div className="panel skeleton" style={{ minHeight: 350 }} />
        <div className="panel skeleton" style={{ minHeight: 350 }} />
      </div>
    </>
  );

  return (
    <AppLayout
      title="Operations dashboard"
      subtitle="A live dispatch briefing for coverage and the next 12 hours."
      actions={headerActions}
    >
      {error ? <div className="feedback error">{error}</div> : null}
      {backgroundError ? (
        <div className="feedback warning" role="status">
          The last briefing is still visible. {backgroundError}
        </div>
      ) : null}

      {loading && !data ? renderSkeleton() : (
        <>
          <section className={`ops-status-banner ops-status-banner--${hasBriefing ? posture.tone : 'unavailable'}`}>
            <div className="ops-status-main">
              <span className="ops-kicker">Next {BRIEFING_WINDOW_HOURS} hours</span>
              <h2>{hasBriefing ? posture.label : 'Briefing unavailable'}</h2>
              <p>
                {data?.headline || 'Live fleet positions remain available while the briefing reconnects.'}
              </p>
            </div>
            <div className="ops-status-actions">
              <span className="ops-updated-at">Updated {formatDateTime(data?.generatedAt)}</span>
              <button
                type="button"
                className="btn btn-subtle"
                onClick={handleRefresh}
                disabled={refreshing || fareRefreshing || fleet.refreshing}
              >
                {refreshing || fareRefreshing || fleet.refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
              <Link to="/operations/intelligence" className="btn btn-subtle">Intelligence</Link>
            </div>
          </section>

          <section className="ops-briefing-metrics" aria-label="Operations summary">
            <MetricTile
              label="Upcoming trips"
              value={hasBriefing ? formatNumber(readiness.upcomingTotal) : '—'}
              hint={`next ${BRIEFING_WINDOW_HOURS} hours`}
              tone={readiness.upcomingTotal ? 'info' : 'neutral'}
            />
            <MetricTile
              label="Dispatch exceptions"
              value={hasBriefing ? formatNumber(dispatchExceptions) : '—'}
              hint={`${formatNumber(metrics.overduePickups)} overdue · ${formatNumber(metrics.unassignedUpcoming)} unassigned`}
              tone={dispatchExceptions ? 'warning' : 'good'}
            />
            <MetricTile
              label="Available now"
              value={formatNumber(fleet.counts.available)}
              hint={`${formatNumber(fleet.counts.offline)} offline`}
              tone={fleet.counts.available ? 'good' : readiness.upcomingTotal ? 'warning' : 'neutral'}
            />
            <MetricTile
              label="On trip"
              value={formatNumber(fleet.counts.onTrip)}
              hint="live vehicle state"
              tone={fleet.counts.onTrip ? 'info' : 'neutral'}
            />
          </section>

          <section className="panel" style={{ marginBottom: 18 }} aria-label="Fleet safety summary">
            <div className="panel-header ops-panel-header">
              <div><span className="ops-section-kicker">This calendar month</span><h3>Driver safety</h3></div>
              <Link to="/reports/driver-safety" className="btn btn-subtle">Full safety report</Link>
            </div>
            <div className="ops-briefing-metrics" style={{ margin: 0 }}>
              <MetricTile label="Fleet average" value={safety?.fleetAverage == null ? 'N/A' : safety.fleetAverage} hint="qualified drivers / 100" tone="info" />
              <MetricTile label="Needs review" value={formatNumber(safety?.driversNeedingReview)} hint="qualified score below 70" tone={safety?.driversNeedingReview ? 'warning' : 'good'} />
              <MetricTile label="Valid mileage" value={Number(safety?.validMiles || 0).toFixed(1)} hint={`${Math.round(Number(safety?.scoredCoverage || 0) * 100)}% speed coverage`} tone="neutral" />
              <MetricTile label="Safety context" value={formatNumber(Number(safety?.context?.sos || 0) + Number(safety?.context?.staleTripCancellations || 0) + Number(safety?.context?.hosAlerts || 0) + Number(safety?.context?.accidents || 0) + Number(safety?.context?.trackingLoss || 0))} hint={`${safety?.context?.sos || 0} SOS · ${safety?.context?.staleTripCancellations || 0} stale · ${safety?.context?.trackingLoss || 0} GPS loss`} tone={safety?.context?.sos ? 'warning' : 'neutral'} />
            </div>
          </section>

          <section className="ops-primary-grid">
            <div className="panel ops-hourly-panel">
              <div className="panel-header ops-panel-header">
                <div>
                  <span className="ops-section-kicker">Today</span>
                  <h3>Trips by hour</h3>
                </div>
                <Link to="/bookings" className="btn btn-subtle">Trip history</Link>
              </div>
              <HourlyTripChart profile={hourlyTrips} />
            </div>

            <div className="panel ops-live-map-panel">
              <div className="panel-header ops-panel-header">
                <div>
                  <span className="ops-section-kicker">{fleet.connected ? 'Live' : 'Reconnecting'}</span>
                  <h3>Fleet view</h3>
                </div>
                <Link to="/operations/dispatch" className="btn btn-subtle">Full map</Link>
              </div>

              <LiveFleetMap
                drivers={fleet.drivers}
                loading={fleet.loading}
                error={fleet.error}
                freshnessNow={fleet.freshnessNow}
                serviceCenter={fleet.serviceCenter}
                serviceZoom={fleet.serviceZoom}
                variant="compact"
              />

              <div className="ops-map-footer">
                <div className="ops-map-legend" aria-label="Vehicle map legend">
                  <span><i className="is-available" />Available</span>
                  <span><i className="is-ontrip" />On trip</span>
                  <span><i className="is-offline" />Offline</span>
                </div>
                {fleet.counts.noLocation > 0 ? (
                  <span>{fleet.counts.noLocation} awaiting location</span>
                ) : (
                  <span>All loaded vehicles located</span>
                )}
              </div>
            </div>
          </section>

          <section className="panel ops-fare-strip">
            <div className="panel-header ops-panel-header">
              <div>
                <span className="ops-section-kicker">Current rate card</span>
                <h3>Fare system</h3>
              </div>
              <Link to="/fares" className="btn btn-subtle">Manage fares</Link>
            </div>

            {fareLoading && !fare ? (
              <div className="ops-fare-rate-grid" aria-label="Loading fare system">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="ops-fare-rate skeleton" style={{ minHeight: 78 }} />
                ))}
              </div>
            ) : fare ? (
              <>
                <div className="ops-fare-rate-grid">
                  <div className="ops-fare-rate">
                    <span>Per mile</span>
                    <strong>${Number(fare.farePerMile || 0).toFixed(2)}</strong>
                  </div>
                  <div className="ops-fare-rate">
                    <span>Extra passenger</span>
                    <strong>${Number(fare.extraPass || 0).toFixed(2)}</strong>
                  </div>
                  <div className="ops-fare-rate">
                    <span>Wait / minute</span>
                    <strong>${Number(fare.waitTimePerMinute || 0).toFixed(2)}</strong>
                  </div>
                </div>
                <div className={`ops-fare-meta${fareError ? ' is-warning' : ''}`}>
                  <span>
                    Base ${Number(fare.baseFare || 0).toFixed(2)} - Minimum ${Number(fare.minimumFare || 0).toFixed(2)}
                  </span>
                  <span>
                    {fareError
                      ? 'Showing the last loaded rate card.'
                      : fare.updatedAt
                        ? `Updated ${formatDateTime(fare.updatedAt)}`
                        : 'Current meter configuration'}
                  </span>
                </div>
              </>
            ) : (
              <EmptyState>{fareError || 'No fare settings are configured yet.'}</EmptyState>
            )}
          </section>

          <section className="panel ops-upcoming-panel">
            <div className="panel-header ops-panel-header">
              <div>
                <span className="ops-section-kicker">What is next</span>
                <h3>Upcoming pickups</h3>
              </div>
              <Link to="/bookings" className="btn btn-subtle">All bookings</Link>
            </div>

            {!hasBriefing ? (
              <EmptyState>Upcoming pickups are unavailable until the briefing reconnects.</EmptyState>
            ) : !pickups.length ? (
              <EmptyState>No active pickups are scheduled in the next {BRIEFING_WINDOW_HOURS} hours.</EmptyState>
            ) : (
              <div className="ops-pickup-list ops-pickup-list--briefing">
                {pickups.map((booking) => (
                  <Link
                    key={booking.id || booking._id || booking.bookingId}
                    to={booking.route || `/bookings/${booking.id || booking._id}`}
                    className="ops-pickup-row"
                  >
                    <div className="ops-pickup-time">
                      <strong>{formatTime(booking.pickupTime)}</strong>
                      <span>{booking.status || 'Pending'}</span>
                    </div>
                    <div className="ops-pickup-main">
                      <strong>{booking.customerName || `Booking ${booking.bookingId || ''}`.trim()}</strong>
                      <span>{booking.pickupAddress || 'Pickup address pending'}</span>
                      <em>{booking.dropoffAddress || 'Drop-off pending'}</em>
                    </div>
                    <div className="ops-pickup-status">
                      <span className={`badge ${isAssigned(booking) ? 'badge-success' : 'badge-warning'}`}>
                        {isAssigned(booking) ? `Cab ${booking.cabNumber || 'assigned'}` : 'Unassigned'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

        </>
      )}
    </AppLayout>
  );
};

export default Dashboard;
